export const prefix = "/api/v1"; // import.meta.env.VITE_API_URL;
const enableDebug = import.meta.env.VITE_IS_DEBUG;

import { store } from "./store";
import { userLogout } from "../../Boilerplate/api";

import { notifications } from "@mantine/notifications";
import { slugify } from "../../Boilerplate/_Shared/slugify";
import i18n from "i18next";
import mixpanel from "mixpanel-browser";
// import mixpanel from "mixpanel-browser";

const jsonHeaders = {
  "content-type": "application/json",
  accept: "application/json",
};

function getAuthHeaders() {
  const authToken = store.getState().authToken;
  if (authToken) return { authorization: `Bearer ${authToken}` };
  return {};
}

// Runs a request that streams JSON objects, invoking callback for each object
export async function jsonApiRequestStreaming(method, uri, values, callback) {
  const authHeader = getAuthHeaders();

  var reqDetails = {
    url: `${prefix}/${uri}`,
    method: method,
    headers: {
      ...jsonHeaders,
      ...authHeader,
    },
    body: values ? JSON.stringify(values) : null,
  };

  if (enableDebug) console.debug("executing STREAMING request", reqDetails);

  const response = await fetch(reqDetails.url, {
    method: reqDetails.method,
    headers: reqDetails.headers,
    body: reqDetails.body,
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");

    buffer = lines.pop(); // Keep the last incomplete line in the buffer

    for (const line of lines) {
      if (line.trim() !== "") {
        try {
          if (enableDebug) console.debug("Received STREAMING line:", line);
          const json = JSON.parse(line);
          if (enableDebug) console.log("Received STREAMING JSON object:", json);

          callback(json);
        } catch (error) {
          console.error("Error parsing JSON:", error);
        }
      }
    }
  }

  // Process any remaining data in the buffer
  if (buffer.trim() !== "") {
    try {
      const json = JSON.parse(buffer);
      if (enableDebug) console.log("Received FINAL JSON object:", json);
      callback(json);
    } catch (error) {
      console.error("Error parsing JSON:", error);
    }
  }
}

async function showRequestError(response) {
  let responseText = await response.text();
  let message = `${response.statusText} (${response.status}): ${responseText}`;

  if (enableDebug) console.debug("response text", responseText);

  let responseJson = null;
  try {
    responseJson = JSON.parse(responseText);
    if (enableDebug) console.debug("responseJson", responseJson);
  } catch (parseError) {
    if (enableDebug)
      console.debug("Failed to parse response as JSON", parseError);
  }

  if (responseJson) {
    notifications.show({
      color: "red",
      autoClose: 10_000,
      title: responseJson.title || "An error occurred!",
      message: responseJson.detail || responseText,
    });
  } else {
    notifications.show({
      color: "red",
      autoClose: 5000,
      title: "An error occurred!",
      message: message,
    });
  }

  return message;
}

/**
 * `options.silent` suppresses the generic red toast so the caller can own the failure — translate
 * it, put it on the field it belongs to. The thrown error carries `status` and `body` either way.
 */
export async function apiRequest(method, uri, values, options = {}) {
  const authHeader = getAuthHeaders();

  var reqDetails = {
    url: `${prefix}/${uri}`,
    method: method,
    headers: {
      ...jsonHeaders,
      ...authHeader,
    },
    body: values ? JSON.stringify(values) : null,
  };

  const response = await fetch(reqDetails.url, {
    method: reqDetails.method,
    headers: reqDetails.headers,
    body: reqDetails.body,
  });

  if (response.status === 401 && authHeader.authorization) {
    console.warn("Unauthorized request, logging out user");
    userLogout(true);
    return null;
  }

  if (!response.ok) {
    const body = options.silent ? await response.text().catch(() => "") : null;
    const message = options.silent
      ? `${response.statusText} (${response.status}): ${body}`
      : await showRequestError(response);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }

  return response;
}

export async function jsonApiRequest(method, uri, values, options) {
  const response = await apiRequest(method, uri, values, options);
  if (!response) return null;

  if (response.status === 204) return {};
  try {
    // parse the JSON response
    const json = await response.json();
    return json;
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return {};
  }
}

export async function jsonPost(uri, values) {
  return await jsonApiRequest("POST", uri, values);
}

export async function jsonGet(uri) {
  return await jsonApiRequest("GET", uri, null);
}

export async function jsonPut(uri, values, options) {
  return await jsonApiRequest("PUT", uri, values, options);
}

export async function jsonDelete(uri) {
  return await jsonApiRequest("DELETE", uri, null);
}

// One logout for the whole app. The shared version also clears the passkey-enrollment gate, and
// keeps a visitor on a public page (signup, activation link, invitation) when an old token from
// an earlier visit expires there, instead of sending them to the login page.
export { userLogout };

export const updateProduct = async (id, dto) =>
  await jsonPut(`products/${id}`, dto);

export const getProducts = async (
  page = 1,
  pageSize = 25,
  mappingId = null,
  importId = null
) => {
  const params = new URLSearchParams();

  if (page) params.append("page", page);
  if (pageSize) params.append("pageSize", pageSize);
  if (mappingId) params.append("mappingId", mappingId);
  if (importId) params.append("importId", importId);

  const queryString = params.toString();
  const url = `products${queryString ? `?${queryString}` : ""}`;

  return await jsonGet(url);
};

export const getMappingDetails = async (mappingId) =>
  await jsonGet(`mappings/${mappingId}`);

export const getProduct = async (id) => await jsonGet(`products/${id}`);
export const getProductStatus = async (id) =>
  await jsonGet(`products/${id}/status`);

// export const getProductTypesPaged = async (marketplaceId, page = 1, pageSize = 25) =>
//   await jsonGet(`marketplaces/${marketplaceId}/types?page=${page}&pageSize=${pageSize}`);

export const getProductTypesAll = async (marketplaceId) =>
  await jsonGet(`marketplaces/${marketplaceId}/types/all`);

export const getProductTypesTree = async (marketplaceId, root = null) =>
  await jsonGet(`marketplaces/${marketplaceId}/types/tree?root=${root}`);

export const getProductType = async (marketplaceId, id) =>
  await jsonGet(`marketplaces/${marketplaceId}/types/${id}`);

export const getMarketplaces = async () => await jsonGet(`marketplaces`);

export const getMarketplace = async (id) =>
  await jsonGet(`admin/marketplaces/${id}`);
export const updateMarketplace = async (id, dto) =>
  await jsonPut(`admin/marketplaces/${id}`, dto);
export const createMarketplace = async (dto) =>
  await jsonPost(`admin/marketplaces`, dto);
export const triggerMarketplaceRefresh = async (id) =>
  await jsonPost(`admin/marketplaces/${id}/bulk-update`);

export const getAbstractMapping = async (marketplaceId) =>
  await jsonGet(`marketplaces/${marketplaceId}/mapping`);

export const updateAbstractMapping = async (marketplaceId, dto) =>
  await jsonPut(`marketplaces/${marketplaceId}/mapping`, dto);

export const getAdminJobs = async (
  page,
  pageSize,
  tenantFilter,
  statusFilter
) =>
  await jsonGet(
    `admin-jobs?page=${page}&pageSize=${pageSize}&tenantFilter=${tenantFilter}&statusFilter=${statusFilter}`
  );

function encodeFileName(fileName) {
  // https://stackoverflow.com/questions/44429173/javascript-encodeuri-failed-to-encode-round-bracket
  return encodeURIComponent(fileName).replace(/[!'()*]/g, function (c) {
    return "%" + c.charCodeAt(0).toString(16);
  });
}

export async function uploadFile(file, type, marketplaceId = null) {
  let authHeaders = getAuthHeaders();

  let simpleFileName = slugify(file.name);
  let uriFileName = encodeFileName(file.name);

  var response = await fetch(
    `${prefix}/imports/${type}/${marketplaceId ?? ""}`,
    {
      method: "POST",
      body: file,
      headers: {
        ...authHeaders,
        "content-type": file.type,
        "content-length": `${file.size}`,
        "content-disposition": `attachment; filename=\"${simpleFileName}\"; filename*=UTF-8''${uriFileName}`,
      },
    }
  );

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }
  return await response.json();
}

/**
 * The two headers the backend's ExportWarningHeaders sets on file downloads: a count plus a
 * base64 UTF-8 JSON array of sentences ("column X will be empty on every row"). Absence means
 * "nothing to say". A malformed header must never break the download that carried it.
 */
export function readExportWarnings(response) {
  try {
    const payload = response?.headers?.get("X-Export-Warnings");
    if (!payload) return [];
    const bytes = Uint8Array.from(atob(payload), (c) => c.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes));
    return Array.isArray(parsed) ? parsed.filter((w) => typeof w === "string") : [];
  } catch {
    return [];
  }
}

async function downloadResponseAsFile(response, fallbackName = "download") {
  if (!response) return false;
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const contentDisposition = response.headers.get("Content-Disposition");
  const filenameMatch = contentDisposition?.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
  );
  const filename = filenameMatch
    ? filenameMatch[1].replace(/['"]/g, "")
    : fallbackName;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
  return true;
}

export async function createExport(mappingId, dto) {
  const uri = mappingId
    ? `mappings/${mappingId}/products/export`
    : `products/export`;
  const response = await apiRequest("POST", uri, dto);
  return await downloadResponseAsFile(response);
}

export async function downloadFile(uri) {
  const response = await apiRequest("GET", uri);
  return await downloadResponseAsFile(response);
}

export const getImportMappings = async (importId) =>
  await jsonGet(`imports/${importId}/mappings`);

export const getImports = async () => await jsonGet(`imports`);

// REMOVE::
export const getImportsAsin = async () => await jsonGet(`imports/asin`);
export const getImportsForeign = async () => await jsonGet(`imports/foreign`);
export const getImportsDomestic = async () => await jsonGet(`imports/domestic`);

export const getImport = async (id) => await jsonGet(`imports/${id}`);
export const deleteImport = async (id) => await jsonDelete(`imports/${id}`);

/// Removes the products an earlier run of this import left behind (the ones flagged
/// notInLatestRun). Returns { jobId, products } — the work runs as a job.
export const removeSupersededProducts = async (id) =>
  await jsonDelete(`imports/${id}/superseded-products`);

export const getSystemMappings = async () => await jsonGet(`mappings/system`);

export const createMapping = async (dto) => await jsonPost(`mappings`, dto);
export const getMappingProfile = async (id) => await jsonGet(`mappings/${id}`);

// Mapping profiles CRUD
export const getMappingProfiles = async () =>
  await jsonGet(`mappings/profiles`);
export const getMappingProfileById = async (id) =>
  await jsonGet(`mappings/profiles/${id}`);
export const createMappingProfile = async (dto) =>
  await jsonPost(`mappings/profiles`, dto);
export const updateMappingProfile = async (id, dto) =>
  await jsonPut(`mappings/profiles/${id}`, dto);
export const deleteMappingProfile = async (id) =>
  await jsonDelete(`mappings/profiles/${id}`);
export const previewMappingProfile = async (id, dto) =>
  await jsonPost(`mappings/profiles/${id}/preview`, dto);
export const executeMappingProfile = async (id, dto) =>
  await jsonPost(`mappings/profiles/${id}/execute`, dto);

// AI-assisted mapping suggestions
// Provide either targetSchemaDefinitionId (for structural fields) or targetPimSchemaId (for attributes)
export const suggestMappings = async (importId, { targetSchemaDefinitionId, targetPimSchemaId }) =>
  await jsonPost(`mappings/suggest`, { importId, targetSchemaDefinitionId, targetPimSchemaId });

// Quick-create a mapping profile from an import to a target schema
// Automatically infers source schema if needed and optionally applies AI suggestions
export const quickCreateMappingProfile = async (importId, targetSchemaId, applyAiSuggestions = true) =>
  await jsonPost(`mappings/profiles/quick-create`, { importId, targetSchemaId, applyAiSuggestions });

export const applyMapping = async (importId, mappingId) =>
  await jsonPost(`imports/${importId}/mapping/${mappingId}/products`);

export const deleteProducts = async (dto) =>
  await jsonPost(`products/delete`, dto);

export const getJobStatus = async (jobId) => await jsonGet(`jobs/${jobId}`);

export const createSuggestion = async (productId, model) =>
  await jsonPost(`products/${productId}/suggestion`, { model });

export const getValueList = async (valueListId) =>
  await jsonGet(`value-lists/${valueListId}`);

export const getValueListByCode = async (marketplaceId, listCode) =>
  await jsonGet(`marketplaces/${marketplaceId}/lists/code/${listCode}`);

export const searchValueListByCode = async (
  marketplaceId,
  listCode,
  query = "",
  limit = 50
) =>
  await jsonGet(
    `marketplaces/${marketplaceId}/lists/code/${listCode}/search?q=${encodeURIComponent(
      query
    )}&limit=${limit}`
  );

export const updateValueList = async (marketplaceId, dto) =>
  await jsonPut(`marketplaces/${marketplaceId}/lists/${dto.id}`, dto);

// PROFILE
export const getProfile = async () => await jsonGet(`profile`);

export const updateProfile = async (dto) => {
  // mixpanel.track("Update profile");
  return await jsonPut(`profile`, dto);
};
// Timezones
export const getTimezones = async () => await jsonGet("timezones");

export const adminImpersonateUser = async (dto) =>
  await jsonPost(`auth/account/impersonate`, dto);

// TENANTS
export const createTenant = async (dto) => await jsonPost(`tenants`, dto);
export const updateTenant = async (id, dto) =>
  await jsonPut(`tenants/${id}`, dto);
export const getTenants = async (page, pageSize) =>
  await jsonGet(`tenants?page=${page}&pageSize=${pageSize}`);
export const getTenant = async (id) => await jsonGet(`tenants/${id}`);

// CREDITS
export const modifyCredits = async (tenantId, dto) => {
  // mix.track("Modify credits", { tenantId });
  return await jsonPost(`tenants/${tenantId}/credits`, dto);
};

export const getAdminJobDetails = async (id) =>
  await jsonGet(`admin-jobs/${id}`);
export const requeueJob = async (id) =>
  await jsonPost(`admin-jobs/${id}/retry`);
export const requeueJobHard = async (id) =>
  await jsonPost(`admin-jobs/${id}/retry-hard`);

export const getTenantProfile = async () => await jsonGet(`tenant/profile`);
export const updateTenantProfile = async (dto) =>
  await jsonPut(`tenant/profile`, dto);

// FacetFlux-specific tenant-wide settings (e.g. default content language)
export const getTenantSettings = async () => await jsonGet(`tenant-settings`);
export const updateTenantSettings = async (dto) =>
  await jsonPut(`tenant-settings`, dto);

// CHECKOUT & PAYMENTS
// export async function getProducts() {
//   return await jsonGet(`payment/stripe/products`);
// }
export async function checkoutSubscription(dto) {
  return await jsonPost(`payment/stripe/checkout-plan`, dto);
}
export async function checkoutCredits(dto) {
  return await jsonPost("payment/stripe/checkout-credits", dto);
}
export async function customerPortal() {
  // mixpanel.track("Customer Portal");
  return await jsonPost("payment/stripe/portal");
}

export function checkout(plan, callback) {
  checkoutSubscription(plan)
    .then((data) => {
      try {
        mixpanel.track("Checkout Plan", {
          plan: plan.planName,
          interval: plan.interval,
        });
      } catch {}
      if (callback) callback();
      location.href = data.redirectUrl;
    })
    .catch((error) => {
      alert("Sorry, something went wrong: " + error);
      if (callback) callback();
    });
}

// USERS
export const getUsers = async (page, pageSize) =>
  await jsonGet(`users?page=${page}&pageSize=${pageSize}`);
export const getSingleUser = async (id) => await jsonGet(`users/${id}`);
export const createUser = async (dto) => await jsonPost(`auth/account`, dto);
export const updateUser = async (id, dto) => await jsonPut(`users/${id}`, dto);
export const deleteUser = async (id) => await jsonDelete(`users/${id}`);

// INVITATIONS
export const sendUserInvitation = async (id) =>
  await jsonPost(`users/${id}/invitations`);

export async function uploadImage(file, imageType) {
  let authHeaders = getAuthHeaders();
  var response = await fetch(`${prefix}/profile/images/${imageType}`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type,
      "content-length": `${file.size}`,
    },
  });
  // mixpanel.track("Upload image", { imageType });
  return await response.json();
}

export const getEsskaTranslationRequest = async (id) =>
  await jsonGet(`esska/translation-requests/${id}`);

export const getEsskaTranslationRequests = async (page, pageSize) =>
  await jsonGet(`esska/translation-requests?page=${page}&pageSize=${pageSize}`);

export const getTranslationUsage = async (month) =>
  await jsonGet(`esska/translation-usage?month=${encodeURIComponent(month)}`);

export const downloadTranslationUsage = (month) =>
  downloadFile(
    `esska/translation-usage?month=${encodeURIComponent(month)}&format=csv`
  );

// Esska Products API
export const getEsskaProducts = async (page, pageSize, categoryId, search, missingTranslation) => {
  let url = `esska/products?page=${page}&pageSize=${pageSize}`;
  if (categoryId) url += `&categoryId=${categoryId}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  if (missingTranslation) url += `&missingTranslation=${encodeURIComponent(missingTranslation)}`;
  return await jsonGet(url);
};

export const getEsskaProduct = async (id) =>
  await jsonGet(`esska/products/${id}`);

export const getEsskaProductByJsonId = async (jsonId) =>
  await jsonGet(`esska/products/by-jsonid/${jsonId}`);

export const getEsskaProductVariants = async (id) =>
  await jsonGet(`esska/products/${id}/variants`);

export const getEsskaProductVariantsByJsonId = async (jsonId) =>
  await jsonGet(`esska/products/by-jsonid/${jsonId}/variants`);

export const retranslateProduct = async (
  productJsonId,
  targetLanguage,
  sourceLanguage = "de"
) =>
  await jsonPost(
    `esska/translations/retranslate/${productJsonId}?targetLanguage=${targetLanguage}&sourceLanguage=${sourceLanguage}`
  );

// Free-text translation API (generic — not tied to product data)
export const getFreeTextTranslationLanguages = async () =>
  await jsonGet(`translations/languages`);

export const quoteFreeTextTranslation = async (payload) =>
  await jsonPost(`translations/quote`, payload);

export const postFreeTextTranslation = async (payload) =>
  await jsonPost(`translations`, payload);

// ============================================================================
// PIM API Functions
// ============================================================================

export const getPimProducts = async (page, pageSize, filters = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortDirection)
    params.append("sortDirection", filters.sortDirection);
  // Callers use both spellings; the catalog's search box passed `search` and was silently dropped.
  if (filters.search || filters.filter)
    params.append("search", filters.search || filters.filter);
  if (filters.sourceSystem) params.append("sourceSystem", filters.sourceSystem);
  if (filters.status) params.append("status", filters.status);
  if (filters.sourceTenantId) params.append("sourceTenantId", filters.sourceTenantId);
  if (filters.purpose) params.append("purpose", filters.purpose);
  if (filters.channelCode) params.append("channelCode", filters.channelCode);
  if (filters.categoryId) params.append("categoryId", filters.categoryId);
  if (filters.attentionReason) params.append("attentionReason", filters.attentionReason);
  if (filters.sourceImportId) params.append("sourceImportId", filters.sourceImportId);

  return await jsonGet(`pim/products?${params.toString()}`);
};

export const getPimProduct = async (id) => await jsonGet(`pim/products/${id}`);

export const createPimProduct = async (data) =>
  await jsonPost(`pim/products`, data);

export const updatePimProduct = async (id, data) =>
  await jsonPut(`pim/products/${id}`, data);

export const updateProductChannels = async (productId, channelCodes) =>
  await jsonPut(`pim/products/${productId}/channels`, { channelCodes });

export const updateProductCategories = async (productId, categoryIds) =>
  await jsonPut(`pim/products/${productId}/categories`, { categoryIds });

// Silent: a 409 here means "that number is taken", which belongs on the input, translated.
export const setProductNumber = async (productId, productNumber) =>
  await jsonPut(`pim/products/${productId}/number`, { productNumber }, { silent: true });

export const getProductChannelReadiness = async (productId) =>
  await jsonGet(`pim/products/${productId}/channel-readiness`);

export const bulkUpdateProductChannels = async (productIds, addChannelCodes, removeChannelCodes) =>
  await jsonPost("pim/products/bulk-channels", { productIds, addChannelCodes, removeChannelCodes });

export const generateProductNumber = async (productId) =>
  await jsonPost(`pim/products/${productId}/generate-number`);

export const deletePimProduct = async (id) =>
  await jsonDelete(`pim/products/${id}`);

export const bulkDeletePimProducts = async (ids) =>
  await jsonPost("pim/products/bulk-delete", { ids });

export const getPimVariants = async (productId, page = 1, pageSize = 50) => {
  // const params = new URLSearchParams({
  //   productId,
  //   page: page.toString(),
  //   pageSize: pageSize.toString(),
  // });
  // return await jsonGet(`pim/variants?${params.toString()}`);
  return await jsonGet(`pim/products/${productId}/variants`);
};

export const getPimVariant = async (id) => await jsonGet(`pim/variants/${id}`);

export const updatePimVariant = async (id, data) =>
  await jsonPut(`pim/variants/${id}`, data);

// Dashboard APIs
export const getPimProductCount = async () =>
  await jsonGet("pim/products/count");
export const getCreditUsageStats = async () =>
  await jsonGet("tenant/credits/usage-stats");

export const getPimAttributes = async (entityType, entityId) => {
  const params = new URLSearchParams({
    entityType,
    entityId,
  });
  return await jsonGet(`pim/attributes?${params.toString()}`);
};

export const getPimAttributeAudit = async (entityType, entityId) => {
  const params = new URLSearchParams({
    entityType,
    entityId,
  });
  return await jsonGet(`pim/attributes/audit?${params.toString()}`);
};

export const getPimAttribute = async (id) =>
  await jsonGet(`pim/attributes/${id}`);

export const updatePimAttribute = async (id, values) =>
  await jsonPut(`pim/attributes/${id}`, { values });

export const createPimAttribute = async (data) =>
  await jsonPost("pim/attributes", data);

export const deletePimAttribute = async (id) =>
  await jsonDelete(`pim/attributes/${id}`);

export const getPimAssets = async (entityType, entityId) =>
  await jsonGet(
    `pim/assets/by-entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
  );

export const getPimAsset = async (id) => await jsonGet(`pim/assets/${id}`);

export const updatePimAsset = async (id, data) =>
  await jsonPut(`pim/assets/${id}`, data);

export const createPimAsset = async (data) =>
  await jsonPost("pim/assets", data);

export const deletePimAsset = async (id) =>
  await jsonDelete(`pim/assets/${id}`);

// ── Smart Import ────────────────────────────────────────────

export async function uploadSmartImportFile(file) {
  const authHeaders = getAuthHeaders();
  const uriFileName = encodeFileName(file.name);

  // Route JSON to existing endpoint, XLSX/CSV to generic file endpoint
  const isJson =
    file.name.endsWith(".json") || file.type === "application/json";
  const endpoint = isJson ? "smart-import/upload" : "smart-import/upload-file";

  const response = await fetch(`${prefix}/${endpoint}`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type || "application/octet-stream",
      "content-length": `${file.size}`,
      "content-disposition": `attachment; filename*=UTF-8''${uriFileName}`,
    },
  });

  if (response.status === 401 && authHeaders.authorization) {
    userLogout(true);
    return null;
  }

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }

  return await response.json();
}

export const getSmartImports = async () => await jsonGet("smart-import");
export const getSmartImport = async (id) => await jsonGet(`smart-import/${id}`);
export const inferSmartImport = async (id) =>
  await jsonPost(`smart-import/${id}/infer`);
export const updateSmartImportSchema = async (id, schema) =>
  await jsonPut(`smart-import/${id}/schema`, schema);
export const executeSmartImport = async (id) =>
  await jsonPost(`smart-import/${id}/execute`);
export const deleteSmartImport = async (id) =>
  await jsonDelete(`smart-import/${id}`);
export const analyzeFromCategory = async (categoryId) =>
  await jsonPost("smart-import/from-category", { categoryId });

// ── Esska Wawi XML import ────────────────────────────────────

export async function uploadEsskaWawiXml(file) {
  const authHeaders = getAuthHeaders();
  const uriFileName = encodeFileName(file.name);

  const response = await fetch(`${prefix}/imports/esska-wawi/upload`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type || "application/xml",
      "content-length": `${file.size}`,
      "content-disposition": `attachment; filename*=UTF-8''${uriFileName}`,
    },
  });

  if (response.status === 401 && authHeaders.authorization) {
    userLogout(true);
    return null;
  }

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }

  return await response.json();
}

export const seedEsskaWawiSchema = async () =>
  await jsonPost("imports/esska-wawi/seed-schema");

export const extractEsskaTechnicalSpecs = async (productId) =>
  await jsonPost(`imports/esska-wawi/products/${productId}/extract-specs`);

/**
 * Trigger a download of the Wawi-shaped XML for a product. We fetch via
 * `apiRequest` (so the bearer token is sent) and then turn the response into
 * a blob URL backed by an invisible anchor click — `window.location` would
 * lose the Authorization header.
 */
export async function downloadEsskaWawiXml(productId, suggestedFileName) {
  const response = await apiRequest(
    "GET",
    `imports/esska-wawi/products/${productId}/export-xml`,
  );
  if (!response) return null;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedFileName || `${productId}.xml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
}

// ── Export-mapping layer (standalone export profiles + serializers) ─────────
// The inverse of the import side: pick a profile for a schema, then export one
// product, a small selection (ZIP), or the whole catalog (a background job).
// Downloads reuse downloadResponseAsFile, which honours the server's
// Content-Disposition filename and falls back to the name we pass. Each download
// wrapper returns the export's WARNINGS (possibly empty) — the server has been
// sending them all along; returning them is what lets the UI finally say
// "downloaded, but column X will be empty".

/** Export profiles applicable to a schema (key, displayName, languages, …). */
export async function getExportProfiles(schemaId) {
  return await jsonGet(`pim/exports/profiles?schemaId=${encodeURIComponent(schemaId)}`);
}

/** Download a single product through an export profile (e.g. Esska translation JSON). */
export async function downloadProductExport(productId, profileKey, language, fileName) {
  const q = new URLSearchParams({ profileKey });
  if (language) q.set("language", language);
  const response = await apiRequest("GET", `pim/exports/products/${productId}?${q.toString()}`);
  const warnings = readExportWarnings(response);
  await downloadResponseAsFile(response, fileName || `${productId}.export`);
  return warnings;
}

/** Download a small selection as a ZIP (server caps the count; larger sets need the catalog job). */
export async function downloadSelectionExport(productIds, profileKey, language, fileName) {
  const response = await apiRequest("POST", "pim/exports/selection", {
    productIds,
    profileKey,
    language,
  });
  const warnings = readExportWarnings(response);
  await downloadResponseAsFile(response, fileName || "export.zip");
  return warnings;
}

/** Enqueue a full-catalog export job; returns { jobId } to poll. */
export async function enqueueCatalogExport(schemaId, profileKey, language) {
  return await jsonPost("pim/exports/catalog", { schemaId, profileKey, language });
}

/** Poll a catalog export job's status. */
export async function getExportJobStatus(jobId) {
  return await jsonGet(`pim/exports/${jobId}/status`);
}

/** Download a finished catalog export ZIP. */
export async function downloadExportArtifact(jobId, fileName) {
  const response = await apiRequest("GET", `pim/exports/${jobId}/download`);
  const warnings = readExportWarnings(response);
  await downloadResponseAsFile(response, fileName || `export-${jobId}.zip`);
  return warnings;
}

/**
 * Imports a supplier category tree into a schema. Raw-body upload with the filename in
 * Content-Disposition, matching the other file endpoints — the extension picks the reader.
 */
export async function importPimCategories(schemaId, file) {
  const authHeaders = getAuthHeaders();
  const uriFileName = encodeFileName(file.name);

  const response = await fetch(
    `${prefix}/pim/categories/import?schemaId=${encodeURIComponent(schemaId)}`,
    {
      method: "POST",
      body: file,
      headers: {
        ...authHeaders,
        "content-type": file.type || "application/octet-stream",
        "content-disposition": `attachment; filename*=UTF-8''${uriFileName}`,
      },
    }
  );

  if (response.status === 401 && authHeaders.authorization) {
    userLogout(true);
    return null;
  }

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }

  return await response.json();
}

export async function uploadEsskaWawiCategories(file) {
  const authHeaders = getAuthHeaders();
  const uriFileName = encodeFileName(file.name);

  const response = await fetch(`${prefix}/imports/esska-wawi/upload-categories`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type || "application/xml",
      "content-length": `${file.size}`,
      "content-disposition": `attachment; filename*=UTF-8''${uriFileName}`,
    },
  });

  if (response.status === 401 && authHeaders.authorization) {
    userLogout(true);
    return null;
  }

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }

  return await response.json();
}
export const inferSubtree = async (parentCategoryId, strategy = null) =>
  await jsonPost("smart-import/infer-subtree", { parentCategoryId, ...(strategy && { strategy }) });
export const retryTreeSession = async (id) =>
  await jsonPost(`smart-import/tree/${id}/retry`);
export const getTreeSession = async (id) =>
  await jsonGet(`smart-import/tree/${id}`);
export const listTreeSessions = async () =>
  await jsonGet("smart-import/tree");

export const checkImportSchema = async (importId, schemaId, enrichmentMode = null) =>
  await jsonGet(
    `imports/${importId}/check-schema/${schemaId}${enrichmentMode ? `?enrichmentMode=${encodeURIComponent(enrichmentMode)}` : ""}`,
  );
export const getImportRowErrors = async (importId) =>
  await jsonGet(`imports/${importId}/row-errors`);
export const getGroupingPreview = async (importId, variantGroupColumn) =>
  await jsonGet(
    `imports/${importId}/grouping-preview${variantGroupColumn ? `?variantGroupColumn=${encodeURIComponent(variantGroupColumn)}` : ""}`,
  );
// Needs the target schema, unlike the grouping preview — the proposals are about which product
// type each group would get, and that only means something once a schema is chosen.
export const getCategoryProposals = async (importId, schemaId, variantGroupColumn) =>
  await jsonGet(
    `imports/${importId}/category-proposals?schemaId=${encodeURIComponent(schemaId)}` +
      (variantGroupColumn ? `&variantGroupColumn=${encodeURIComponent(variantGroupColumn)}` : ""),
  );
export const startAugmentedImport = async (
  importId,
  { schemaId, role, productTypeId, variantGroupColumn, enrichmentMode, productTypeByGroup },
) =>
  await jsonPost(`imports/${importId}/augmented-import`, {
    schemaId,
    role,
    productTypeId,
    variantGroupColumn,
    enrichmentMode,
    productTypeByGroup,
  });
export const executeTreeSession = async (id) =>
  await jsonPost(`smart-import/tree/${id}/execute`);
export const updateTreeSessionTraits = async (id, proposedTraits) =>
  await jsonPut(`smart-import/tree/${id}/traits`, proposedTraits);
export const reprocessTreeSession = async (id) =>
  await jsonPost(`smart-import/tree/${id}/reprocess`);
export const getEsskaCategories = async () =>
  await jsonGet("esska/categories");

// VALIDATION
export const validateProduct = async (productId, schemaId = null) => {
  const url = schemaId
    ? `pim/products/${productId}/validate?schemaId=${schemaId}`
    : `pim/products/${productId}/validate`;
  return await jsonPost(url, {});
};

export const validateData = async (schemaId, data, isStrict = false) =>
  await jsonPost(`validation/report`, { schemaId, data, isStrict });

export const validateBatch = async (schemaId, items, isStrict = false) =>
  await jsonPost(`validation/batch`, { schemaId, items, isStrict });

// OPTIVISE INTEGRATION
export const testOptiviseConnection = async (optiviseApiUrl, optiviseApiKey) =>
  await jsonPost(`integrations/optivise/test-connection`, {
    optiviseApiUrl,
    optiviseApiKey,
    marketplaceId: "test",
  });

export const syndicateToOptivise = async (
  productIds,
  marketplaceId,
  optiviseApiUrl,
  optiviseApiKey
) =>
  await jsonPost(`integrations/optivise/syndicate`, {
    productIds,
    marketplaceId,
    optiviseApiUrl,
    optiviseApiKey,
  });

// AI ENRICHMENT
// Enrichment is queued, not awaited: these return a run ({ jobId, suggestionIds, ... }) as soon as
// the suggestion rows exist. Poll getEnrichmentSuggestion (single) or getEnrichmentRun (batch).
export const enrichProduct = async (productId, mode = "Standard") =>
  await jsonPost(`pim/products/${productId}/enrich?mode=${mode}`);

export const getEnrichmentRun = async (jobId) =>
  await jsonGet(`pim/products/enrichment/runs/${jobId}`);

export const getEnrichmentSuggestion = async (productId) => {
  // Raw fetch rather than jsonGet: a 404 is the normal "not enriched yet" answer and must not
  // raise an error notification. Auth still goes through getAuthHeaders — hand-rolling the header
  // here is what broke it: it read a `useStore` that api.js never imports, so every call threw a
  // ReferenceError into the empty catch below and returned null. Pending suggestions therefore
  // never survived a page reload; they only ever appeared in the session that produced them,
  // because that path sets the state straight from the enrich response.
  try {
    const response = await fetch(`${prefix}/pim/products/${productId}/enrichment`, {
      headers: { ...getAuthHeaders() },
    });
    if (response.status === 404) return null;
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

export const acceptEnrichmentField = async (productId, suggestionId, attributeCode) =>
  await jsonPost(`pim/products/${productId}/enrichment/${suggestionId}/accept`, { attributeCode });

export const acceptAllEnrichment = async (productId, suggestionId) =>
  await jsonPost(`pim/products/${productId}/enrichment/${suggestionId}/accept`, { all: true });

export const rejectEnrichmentField = async (productId, suggestionId, attributeCode) =>
  await jsonPost(`pim/products/${productId}/enrichment/${suggestionId}/reject`, { attributeCode });

export const estimateEnrichmentCost = async (productIds, mode = "Standard") =>
  await jsonPost("pim/products/enrichment/estimate", { productIds, mode });

export const batchEnrichProducts = async (productIds, mode = "Standard") =>
  await jsonPost("pim/products/enrichment/batch", { productIds, mode });

// STANDARDS COMPLIANCE
export const getComplianceReports = async (productTypeId) =>
  await jsonGet(`pim/product-types/${productTypeId}/compliance`);

export const getComplianceReport = async (productTypeId, standardId, classCode) =>
  await jsonGet(`pim/product-types/${productTypeId}/compliance/${standardId}/${classCode}`);

// ── Catalog Diagnostics (free "/diagnose" funnel) ───────────────────────────

// Raw-body upload — same mechanics as the /imports upload (file body +
// Content-Disposition). Errors are NOT toasted here on purpose: the diagnose
// page renders 400/402/409/429 inline (the 402 body is a plain message string
// that explains the repeat cost).
export async function uploadDiagnosticFile(file) {
  const authHeaders = getAuthHeaders();
  const simpleFileName = slugify(file.name);
  const uriFileName = encodeFileName(file.name);
  // Report prose (narrative, issues, next steps) is generated in the user's UI language.
  const language = (i18n.language || "en").split("-")[0];

  const response = await fetch(`${prefix}/diagnostics?language=${encodeURIComponent(language)}`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type || "application/octet-stream",
      "content-length": `${file.size}`,
      "content-disposition": `attachment; filename="${simpleFileName}"; filename*=UTF-8''${uriFileName}`,
    },
  });

  if (response.status === 401 && authHeaders.authorization) {
    userLogout(true);
    return null;
  }

  if (!response.ok) {
    let text = await response.text();
    try {
      const json = JSON.parse(text);
      // ASP.NET serializes the plain-string 402/409 messages as JSON strings.
      text = typeof json === "string" ? json : json.detail || json.title || text;
    } catch {
      // non-JSON body — keep as-is
    }
    const error = new Error(text || `Upload failed (${response.status})`);
    error.status = response.status;
    throw error;
  }

  return await response.json(); // { id, wasFree }
}

// Quiet poll target — no error notification per tick; a transient network
// blip during the 2.5s poll loop must not spam toasts.
export async function getDiagnostic(id) {
  const authHeaders = getAuthHeaders();
  const response = await fetch(`${prefix}/diagnostics/${id}`, {
    headers: { ...jsonHeaders, ...authHeaders },
  });
  if (response.status === 401 && authHeaders.authorization) {
    userLogout(true);
    return null;
  }
  if (!response.ok) throw new Error(`Diagnostic poll failed (${response.status})`);
  return await response.json();
}

export const getDiagnosticReport = async (id) =>
  await jsonGet(`diagnostics/${id}/report`);

export const getDiagnostics = async () => await jsonGet(`diagnostics`);

// CLASSIFICATION STANDARDS (admin)
export const getClassificationStandards = async () =>
  await jsonGet("pim/classification-standards");

export const getClassificationClasses = async (standardId, parentCode = null, q = null) => {
  const params = new URLSearchParams();
  if (parentCode) params.set("parentCode", parentCode);
  if (q) params.set("q", q);
  return await jsonGet(`pim/classification-standards/${standardId}/classes?${params}`);
};

export const getClassificationClass = async (standardId, code) =>
  await jsonGet(`pim/classification-standards/${standardId}/classes/${code}`);

export async function importEtimCsv(file, version = "10.0") {
  const authHeaders = getAuthHeaders();
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${prefix}/admin/classification-standards/import-etim-csv?version=${encodeURIComponent(version)}`,
    {
      method: "POST",
      body: formData,
      headers: { ...authHeaders },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `Import failed: ${response.status}`);
  }

  return await response.json();
}

/**
 * Applies the language versions of an official ETIM IXF file to an already-imported standard.
 * Only labels and synonyms are touched — the class/feature structure stays as it is.
 */
export async function importIxfTranslations(standardId, file, languages = "") {
  const authHeaders = getAuthHeaders();
  const formData = new FormData();
  formData.append("file", file);

  const query = languages ? `?languages=${encodeURIComponent(languages)}` : "";
  const response = await fetch(
    `${prefix}/admin/classification-standards/${standardId}/import-ixf-translations${query}`,
    {
      method: "POST",
      body: formData,
      headers: { ...authHeaders },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `Import failed: ${response.status}`);
  }

  return await response.json();
}
